<script>
  import { onMount, onDestroy } from "svelte";
  import Icon from "@iconify/svelte";
  import folderIcon from "@iconify/icons-mdi/folder";
  import filesIcon from "@iconify/icons-mdi/folder-multiple";
  import fileIcon from "@iconify/icons-mdi/file-outline";
  import imageIcon from "@iconify/icons-mdi/image";
  import pdfIcon from "@iconify/icons-mdi/file-pdf-box";
  import videoIcon from "@iconify/icons-mdi/video";
  import docIcon from "@iconify/icons-mdi/file-document";
  import zipIcon from "@iconify/icons-mdi/zip-box";
  import gridIcon from "@iconify/icons-mdi/view-grid";
  import listIcon from "@iconify/icons-mdi/view-list";
  import chevronRight from "@iconify/icons-mdi/chevron-right";
  import homeIcon from "@iconify/icons-mdi/home";
  import arrowUpIcon from "@iconify/icons-mdi/arrow-up";
  import arrowDownIcon from "@iconify/icons-mdi/arrow-down";
  import arrowLeftIcon from "@iconify/icons-mdi/arrow-left";
  import openInNewIcon from "@iconify/icons-mdi/open-in-new";
  import downloadIcon from "@iconify/icons-mdi/download";
  import deleteIcon from "@iconify/icons-mdi/delete-outline";
  import uploadIcon from "@iconify/icons-mdi/upload";
  import refreshIcon from "@iconify/icons-mdi/refresh";

  import FileDetail from "./FileDetail.svelte";
  import AlertStack from "./AlertStack.svelte";

  export let apiBase = "";
  export let apiKey = "";
  export let bucket = "";
  export let permissions = { download: true, rename: true, delete: true };
  export let paused = false;
  export let setRefresh = () => {};
  export let onUnauthorized = () => {};

  let objects = [];
  let folders = [];
  let currentPath = "";
  let loading = false;
  let loadingMore = false;
  let viewMode = "list"; // "list" or "grid"
  let selectedFile = null;
  let fileDetail = null;
  let page = 1;
  let pageSize = 100;
  let nextPage = 0;
  let hasMore = false;
  let searchQuery = "";
  let sortBy = "name";
  let sortDir = "asc";
  let pollInterval = null;
  let loadMoreSentinel = null;
  let loadMoreObserver = null;
  let alerts = [];
  let nextAlertId = 1;
  let showDeleteModal = false;
  let deleteTarget = null;
  let deleteIsFolder = false;
  let deleteBusy = false;
  let breadcrumbs = [];
  let previewUrls = {};
  let previewLoading = {};
  let bucketLabel = "";
  let uploadQueue = [];
  let uploadWorkerActive = false;
  let uploadInput = null;
  let nextUploadId = 1;
  let showUploadQueue = true;
  let prevBucket = "";
  let fetchSeq = 0;
  let folderSizes = {};

  function authHeaders() {
    return apiKey ? { Authorization: `Bearer ${apiKey}` } : {};
  }

  function encodePath(path) {
    if (!path) return "";
    const parts = path.split("/").filter(Boolean).map(encodeURIComponent);
    return parts.join("/") + (path.endsWith("/") ? "/" : "");
  }

  async function readError(res) {
    try {
      const data = await res.json();
      if (data?.error) return data.error;
    } catch {}
    return `HTTP ${res.status}`;
  }

  function pushError(message) {
    alerts = [
      { id: nextAlertId++, message, type: "error" },
      ...alerts
    ].slice(0, 6);
  }

  function formatBytes(bytes) {
    if (bytes === null || bytes === undefined) return "0 B";
    const n = Number(bytes);
    if (!Number.isFinite(n) || n <= 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.min(Math.floor(Math.log(n) / Math.log(k)), sizes.length - 1);
    const val = n / Math.pow(k, i);
    return `${val.toFixed(val >= 10 ? 0 : 1)} ${sizes[i]}`;
  }

  function getObjectSizeLabel(obj) {
    return obj?.size_human || formatBytes(obj?.size_bytes ?? obj?.size ?? obj?.bytes);
  }

  function dismissAlert(id) {
    alerts = alerts.filter(alert => alert.id !== id);
  }

  function enqueueUploadFiles(list) {
    const files = Array.from(list || []).filter(Boolean);
    if (!files.length) return;
    const newJobs = files.map((file) => ({
      id: nextUploadId++,
      file,
      name: file.name || "unnamed",
      size: file.size || 0,
      status: "queued",
      progress: 0,
      error: ""
    }));
    uploadQueue = [...uploadQueue, ...newJobs];
    showUploadQueue = true;
    startUploadWorker();
  }

  function handleUploadSelect(event) {
    enqueueUploadFiles(event?.target?.files || []);
    if (event?.target) event.target.value = "";
  }

  function startUploadWorker() {
    if (uploadWorkerActive) return;
    uploadWorkerActive = true;
    runNextUpload();
  }

  async function runNextUpload() {
    const next = uploadQueue.find(job => job.status === "queued");
    if (!next) {
      uploadWorkerActive = false;
      if (uploadQueue.length > 0 && uploadQueue.every(job => job.status === "done")) {
        showUploadQueue = false;
      }
      return;
    }

    uploadQueue = uploadQueue.map(job => (
      job.id === next.id ? { ...job, status: "uploading", progress: 0, error: "" } : job
    ));

    try {
      await uploadJob(next);
      uploadQueue = uploadQueue.map(job => (
        job.id === next.id ? { ...job, status: "done", progress: 100 } : job
      ));
      fetchObjects(true);
    } catch (err) {
      uploadQueue = uploadQueue.map(job => (
        job.id === next.id ? { ...job, status: "error", error: err?.message || String(err) } : job
      ));
      pushError(err?.message || String(err));
    } finally {
      runNextUpload();
    }
  }

  function uploadJob(job) {
    return new Promise((resolve, reject) => {
      if (!bucket) {
        reject(new Error("No bucket selected"));
        return;
      }
      const key = currentPath ? `${currentPath}${job.name}` : job.name;
      const encodedPath = encodePath(key);
      const url = `${apiBase}/buckets/${encodeURIComponent(bucket)}/${encodedPath}`;
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      xhr.responseType = "json";
      xhr.timeout = 30 * 60 * 1000;

      if (apiKey) {
        xhr.setRequestHeader("Authorization", `Bearer ${apiKey}`);
      }

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100);
          uploadQueue = uploadQueue.map(item => (
            item.id === job.id ? { ...item, progress: Math.max(1, Math.min(99, pct)) } : item
          ));
        }
      };

      xhr.onload = () => {
        if (xhr.status === 401) {
          onUnauthorized("Session expired");
          reject(new Error("Authentication required"));
          return;
        }
        if (xhr.status === 403) {
          const msg = xhr.response?.error || "Uploads are not allowed";
          reject(new Error(msg));
          return;
        }
        if (xhr.status < 200 || xhr.status >= 300) {
          const msg = xhr.response?.error || `Upload failed (HTTP ${xhr.status})`;
          reject(new Error(msg));
          return;
        }
        resolve(xhr.response);
      };

      xhr.onerror = () => reject(new Error("Upload failed"));
      xhr.ontimeout = () => reject(new Error("Upload timed out"));

      const formData = new FormData();
      formData.append("file", job.file, job.name);
      xhr.send(formData);
    });
  }

  async function fetchObjects(reset = true) {
    if (!bucket) return;

    const requestId = ++fetchSeq;
    const requestPath = currentPath;
    const requestBucket = bucket;

    if (reset) {
      loading = true;
      page = 1;
      nextPage = 0;
    } else {
      loadingMore = true;
    }

    try {
      const encodedPath = encodePath(requestPath);
      const baseUrl = `${apiBase}/buckets/${encodeURIComponent(requestBucket)}${encodedPath ? `/${encodedPath}` : ""}`;
      const query = new URLSearchParams();
      query.set("page", String(reset ? 1 : nextPage || page + 1));
      query.set("page_size", String(pageSize));
      if (searchQuery.trim()) query.set("q", searchQuery.trim());
      query.set("order_by", sortBy);
      query.set("dir", sortDir);
      const url = `${baseUrl}?${query.toString()}`;

      const res = await fetch(url, {
        headers: { Accept: "application/json", ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized("Session expired");
        return;
      }

      if (!res.ok) throw new Error(await readError(res));
      const data = await res.json();
      if (requestId !== fetchSeq || requestBucket !== bucket || requestPath !== currentPath) {
        return;
      }

      const incomingObjects = data.objects || [];
      const incomingFolders = data.common_prefixes || [];
      const normalizedObjects = incomingObjects.map((obj) => ({
        ...obj,
        size_human: obj?.size_human || formatBytes(obj?.size_bytes ?? obj?.size ?? obj?.bytes)
      }));

      if (reset) {
        objects = normalizedObjects;
        folders = incomingFolders;
        folderSizes = {};
      } else {
        objects = [...objects, ...normalizedObjects];
        folders = [...folders, ...incomingFolders];
      }

      prefetchPreviewImages(normalizedObjects);
      void loadFolderSizes(incomingFolders, requestBucket);

      if (typeof data.path === "string") {
        const responsePath = normalizePath(data.path);
        if (responsePath === requestPath) {
          currentPath = responsePath;
        }
      }
      if (typeof data.bucket === "string" && data.bucket.trim()) {
        bucketLabel = data.bucket.trim();
      } else if (bucket) {
        bucketLabel = bucket;
      }
      page = data.page || page;
      nextPage = data.next_page || 0;
      hasMore = nextPage > 0;
    } catch (e) {
      if (requestId === fetchSeq) {
        pushError(e?.message || String(e));
      }
    } finally {
      if (requestId === fetchSeq) {
        loading = false;
        loadingMore = false;
      }
    }
  }

  function normalizePath(rawPath) {
    if (!rawPath) return "";
    let path = decodeURIComponent(rawPath.toString());
    path = path.replace(/^\//, "");
    if (path && !path.endsWith("/")) {
      path += "/";
    }
    return path;
  }

  function navigateTo(path, opts = {}) {
    const normalized = normalizePath(path);
    currentPath = normalized;
    selectedFile = null;
    fileDetail = null;
    fetchObjects(true);
    
    // Update URL without reloading (optional, if you want deep linking)
    const newUrl = new URL(window.location);
    if (opts.clearQuery) {
      newUrl.search = "";
    } else if (normalized) {
      newUrl.searchParams.set("path", normalized);
    } else {
      newUrl.searchParams.delete("path");
    }
    window.history.pushState({ path: normalized }, "", newUrl);
  }

  function clearPathQuery() {
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete("path");
    window.history.replaceState({ path: "" }, "", newUrl);
  }

  function handlePopState(event) {
    const urlParams = new URLSearchParams(window.location.search);
    const urlPath = normalizePath(urlParams.get("path"));
    const nextPath = event.state?.path !== undefined ? normalizePath(event.state.path) : urlPath;
    if (nextPath !== undefined) {
      currentPath = nextPath;
      selectedFile = null;
      fileDetail = null;
      fetchObjects(true);
    }
  }

  onMount(() => {
    setRefresh(refresh);
    
    // Set prevBucket to current bucket to prevent reactive block from double-fetching
    prevBucket = bucket;
    bucketLabel = bucket;
    
    // Check initial URL param
    const urlParams = new URLSearchParams(window.location.search);
    const initialPath = normalizePath(urlParams.get("path"));
    currentPath = initialPath || "";

    fetchObjects(true);
    startPolling();
    loadMoreObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some(entry => entry.isIntersecting)) {
          loadMore();
        }
      },
      { root: null, rootMargin: "240px" }
    );
    if (loadMoreSentinel) {
      loadMoreObserver.observe(loadMoreSentinel);
    }
    
    const handleRootNav = (event) => {
      navigateTo("", { clearQuery: !!event?.detail?.clearQuery });
    };
    const handleBucketNav = () => {
      navigateTo("");
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("depot-nav-root", handleRootNav);
    window.addEventListener("depot-nav-bucket", handleBucketNav);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("depot-nav-root", handleRootNav);
      window.removeEventListener("depot-nav-bucket", handleBucketNav);
    };
  });

  onDestroy(() => {
    if (pollInterval) clearInterval(pollInterval);
    if (loadMoreObserver) loadMoreObserver.disconnect();
  });

  function navigateUp() {
    const parts = currentPath.split("/").filter(Boolean);
    parts.pop();
    navigateTo(parts.length ? parts.join("/") + "/" : "");
  }

  function navigateBack() {
    if (window.history.state?.path !== undefined && window.history.length > 1) {
      window.history.back();
    } else {
      navigateUp();
    }
  }

  async function selectFile(obj) {
    selectedFile = obj;
    fileDetail = null;

    try {
      const url = `${apiBase}/object?bucket=${encodeURIComponent(bucket)}&key=${encodeURIComponent(obj.key)}`;
      const res = await fetch(url, {
        headers: { Accept: "application/json", ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized("Session expired");
        return;
      }

      if (!res.ok) throw new Error(await readError(res));
      fileDetail = await res.json();
    } catch (e) {
      pushError(e?.message || String(e));
    }
  }

  function requestDelete(target) {
    deleteTarget = target;
    deleteIsFolder = target?.type === "folder";
    showDeleteModal = true;
  }

  async function confirmDelete() {
    if (!deleteTarget || deleteBusy) return;
    deleteBusy = true;

    try {
      const encodedPath = encodePath(deleteTarget.key);
      const url = `${apiBase}/buckets/${encodeURIComponent(bucket)}/${encodedPath}`;
      const res = await fetch(url, {
        method: "DELETE",
        headers: { Accept: "application/json", ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized("Session expired");
        return;
      }

      if (!res.ok) throw new Error(await readError(res));

      if (deleteIsFolder) {
        folders = folders.filter(f => f !== deleteTarget.key);
      } else {
        objects = objects.filter(o => o.key !== deleteTarget.key);
        if (selectedFile?.key === deleteTarget.key) {
          selectedFile = null;
          fileDetail = null;
        }
      }
      showDeleteModal = false;
      deleteTarget = null;
    } catch (e) {
      pushError(e?.message || String(e));
    } finally {
      deleteBusy = false;
    }
  }

  function cancelDelete() {
    if (deleteBusy) return;
    showDeleteModal = false;
    deleteTarget = null;
  }

  async function openSignedUrl(obj, mode = "view") {
    if (!permissions.download) return;
    try {
      const url = `${apiBase}/object/url?bucket=${encodeURIComponent(bucket)}&key=${encodeURIComponent(obj.key)}`;
      const res = await fetch(url, {
        headers: { Accept: "application/json", ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized("Session expired");
        return;
      }

      if (!res.ok) throw new Error(await readError(res));
      const data = await res.json();
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        throw new Error("Signed URL not available");
      }
    } catch (e) {
      pushError(e?.message || String(e));
    }
  }

  function isImageKey(key = "", contentType = "") {
    const ext = key.split(".").pop()?.toLowerCase();
    const type = contentType?.toLowerCase() || "";
    return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif", "avidf"].includes(ext) ||
      type.startsWith("image/");
  }

  async function ensurePreview(obj) {
    if (!permissions.download) return;
    const key = obj?.key;
    if (!key || previewUrls[key] || previewLoading[key]) return;

    previewLoading = { ...previewLoading, [key]: true };
    try {
      const url = `${apiBase}/object/url?bucket=${encodeURIComponent(bucket)}&key=${encodeURIComponent(key)}`;
      const res = await fetch(url, {
        headers: { Accept: "application/json", ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized("Session expired");
        return;
      }

      if (!res.ok) throw new Error(await readError(res));
      const data = await res.json();
      if (data?.url) {
        previewUrls = { ...previewUrls, [key]: data.url };
      }
    } catch (e) {
      pushError(e?.message || String(e));
    } finally {
      const { [key]: _, ...rest } = previewLoading;
      previewLoading = rest;
    }
  }

  function prefetchPreviewImages(items) {
    if (!permissions.download) return;
    const candidates = items.filter(item => isImageKey(item.key, item.content_type));
    candidates.slice(0, 24).forEach((item) => {
      ensurePreview(item);
    });
  }

  function getFileIconMeta(key, contentType = "") {
    const ext = key.split(".").pop()?.toLowerCase();
    const type = contentType?.toLowerCase() || "";

    if (
      ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif", "avidf"].includes(ext) ||
      type.startsWith("image/")
    ) {
      return { icon: imageIcon, color: "text-sky-400" };
    }
    if (["pdf"].includes(ext) || type === "application/pdf") {
      return { icon: pdfIcon, color: "text-red-400" };
    }
    if (["ppt", "pptx"].includes(ext)) {
      return { icon: docIcon, color: "text-yellow-400" };
    }
    if (["doc", "docx"].includes(ext)) {
      return { icon: docIcon, color: "text-blue-400" };
    }
    if (["xls", "xlsx", "csv"].includes(ext)) {
      return { icon: docIcon, color: "text-emerald-400" };
    }
    if (["mp4", "webm", "mov", "avi", "mkv", "m4v"].includes(ext) || type.startsWith("video/")) {
      return { icon: videoIcon, color: "text-violet-400" };
    }
    if (
      ["zip", "rar", "7z", "tar", "gz", "tgz", "bz2"].includes(ext) ||
      [
        "application/zip",
        "application/x-zip-compressed",
        "application/x-7z-compressed",
        "application/x-rar-compressed",
        "application/x-tar",
        "application/gzip"
      ].includes(type)
    ) {
      return { icon: zipIcon, color: "text-zinc-400" };
    }
    return { icon: fileIcon, color: "text-zinc-400" };
  }

  function getFileName(key) {
    const parts = key.split("/");
    return parts[parts.length - 1] || key;
  }

  function getFolderName(prefix) {
    const trimmed = prefix.replace(currentPath, "").replace(/\/$/, "");
    if (!trimmed) return prefix.replace(/\/$/, "");
    return trimmed;
  }

  function getFolderSizeLabel(prefix) {
    return folderSizes[prefix] || "…";
  }

  async function loadFolderSizes(prefixes, bucketName) {
    const pending = prefixes.filter((prefix) => prefix && !folderSizes[prefix]);
    if (pending.length === 0) return;

    const updates = await Promise.all(
      pending.map(async (prefix) => {
        try {
          const res = await fetch(
            `${apiBase}/bucket-stats?bucket=${encodeURIComponent(bucketName)}&prefix=${encodeURIComponent(prefix)}`,
            { headers: { Accept: "application/json", ...authHeaders() } }
          );

          if (res.status === 401) {
            onUnauthorized("Session expired");
            return null;
          }
          if (!res.ok) return [prefix, "—"];

          const data = await res.json();
          return [prefix, data?.size_human || formatBytes(data?.total_size)];
        } catch {
          return [prefix, "—"];
        }
      })
    );

    const next = {};
    for (const entry of updates) {
      if (!entry) continue;
      next[entry[0]] = entry[1];
    }
    if (Object.keys(next).length > 0) {
      folderSizes = { ...folderSizes, ...next };
    }
  }

  function getBreadcrumbs(pathValue, label, bucketName) {
    const parts = (pathValue || "").split("/").filter(Boolean);
    const rootLabel = label || bucketName || "Bucket";
    const crumbs = [{ label: rootLabel, path: "" }];

    let builtPath = "";
    for (const part of parts) {
      builtPath += part + "/";
      crumbs.push({ label: part, path: builtPath });
    }

    return crumbs;
  }

  function refresh() {
    fetchObjects(true);
  }

  function loadMore() {
    if (!hasMore || loadingMore || loading) return;
    fetchObjects(false);
  }

  let searchTimer = null;
  function handleSearchInput(e) {
    searchQuery = e.target.value;
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => fetchObjects(true), 300);
  }

  function toggleSort(field) {
    if (sortBy === field) {
      sortDir = sortDir === "asc" ? "desc" : "asc";
    } else {
      sortBy = field;
      sortDir = "asc";
    }
    fetchObjects(true);
  }

  function handlePageSizeChange(e) {
    pageSize = Number(e.target.value || 100) || 100;
    fetchObjects(true);
  }

  function startPolling() {
    if (pollInterval) clearInterval(pollInterval);
    pollInterval = setInterval(() => {
      if (!paused) {
        fetchObjects(true);
      }
    }, 5000);
  }

  // Handle bucket changes - reset state when bucket changes
  $: if (bucket && bucket !== prevBucket) {
    // Bucket changed - reset state
    prevBucket = bucket;
    bucketLabel = bucket;
    currentPath = "";
    clearPathQuery();
    objects = [];
    folders = [];
    previewUrls = {};
    previewLoading = {};
    selectedFile = null;
    fileDetail = null;
    searchQuery = "";
    page = 1;
    nextPage = 0;
    hasMore = false;
    fetchObjects(true);
  }

  $: if (loadMoreObserver && loadMoreSentinel) {
    loadMoreObserver.observe(loadMoreSentinel);
  }

  $: breadcrumbs = getBreadcrumbs(currentPath, bucketLabel, bucket);
</script>

<div class="p-4 sm:p-6">
  <div class="flex flex-col lg:flex-row gap-6">
    <div class="flex-1 min-w-0">
      <div class="ui-card">
        <!-- Toolbar -->
        <div class="p-6 border-b border-white/10">
          <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div class="flex items-start gap-3">
              <button
                type="button"
                class="inline-flex h-9 w-9 items-center justify-center border border-lime-400/60 bg-lime-400/60 text-lime-50 rounded-md"
                onclick={() => navigateTo("")}
                title="Bucket root"
              >
                <Icon icon="entypo:bucket" width="18" height="18" />
              </button>

              <div>
                <h1 class="text-lg font-semibold tracking-wide text-zinc-100">
                  {bucket || "Bucket"}
                </h1>
                <p class="mt-0.5 text-sm text-zinc-400">
                  Manage your files and folders
                </p>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-2 min-w-0"></div>

            <div class="flex flex-wrap items-center gap-2 justify-end">
                <button
                  type="button"
                  class="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-zinc-100 hover:bg-white/10 transition-colors disabled:opacity-60"
                  onclick={refresh}
                  disabled={loading || loadingMore}
                  title="Refresh"
                  aria-label="Refresh"
                >
                  <Icon icon={refreshIcon} width="16" height="16" />
                </button>
                {#if permissions.upload}
                  <button
                    type="button"
                    class="h-8 px-3 rounded border border-sky-100/30 bg-sky-400/60 text-sky-100 text-sm hover:bg-sky-400/20 transition-colors"
                    onclick={() => uploadInput?.click()}
                  >
                    <span class="inline-flex items-center gap-2">
                      <Icon icon={uploadIcon} width="16" height="16" />
                      Upload
                    </span>
                  </button>
                  <input
                    bind:this={uploadInput}
                    type="file"
                    multiple
                    class="hidden"
                    onchange={handleUploadSelect}
                  />
                {/if}
                <input
                  type="text"
                  value={searchQuery}
                  placeholder="Filter by name..."
                  class="h-10 w-56 max-w-full rounded-lg border border-white/10 bg-black/30 px-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-sky-400/20"
                  oninput={handleSearchInput}
                />
                <select
                  class="h-10 rounded-lg border border-white/10 bg-black/30 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-sky-400/20"
                  value={pageSize}
                  onchange={handlePageSizeChange}
                >
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="300">300</option>
                </select>

                <div class="flex items-center gap-1 bg-white/5 rounded-lg p-1">
                  <button
                    type="button"
                    class={`p-2 rounded ${viewMode === "list" ? "bg-white/10" : ""}`}
                    onclick={() => viewMode = "list"}
                  >
                    <Icon icon={listIcon} width="16" height="16" />
                  </button>
                  <button
                    type="button"
                    class={`p-2 rounded ${viewMode === "grid" ? "bg-white/10" : ""}`}
                    onclick={() => viewMode = "grid"}
                  >
                    <Icon icon={gridIcon} width="16" height="16" />
                  </button>
                </div>
              </div>
            </div>

          <div class="mt-2 flex items-center gap-1.5 text-[13px] text-zinc-500">
            <div class="flex items-center gap-1">
              <button
                type="button"
                class={`p-1.5 rounded-md transition-colors ${!currentPath ? "opacity-40 cursor-not-allowed text-zinc-600" : "text-zinc-100 hover:bg-white/10"}`}
                title="Back"
                onclick={navigateBack}
                disabled={!currentPath}
              >
                <Icon icon={arrowLeftIcon} width="14" height="14" />
              </button>
              <button
                type="button"
                class={`p-1.5 rounded-md transition-colors ${!currentPath ? "opacity-40 cursor-not-allowed text-zinc-600" : "text-zinc-100 hover:bg-white/10"}`}
                title="Up"
                onclick={navigateUp}
                disabled={!currentPath}
              >
                <Icon icon={arrowUpIcon} width="14" height="14" />
              </button>
            </div>
            <span class="uppercase tracking-wide">Path</span>
            <nav class="flex items-center gap-0.5 overflow-x-auto min-w-0">
              {#each breadcrumbs as crumb, i}
                {#if i > 0}
                <span class="text-zinc-500/70 px-1">/</span>
                {/if}
                <button
                  type="button"
                  class="px-1 py-0.5 rounded hover:bg-white/5 transition-colors truncate max-w-[12rem]"
                  class:text-zinc-300={i === breadcrumbs.length - 1}
                  class:text-zinc-500={i !== breadcrumbs.length - 1}
                  onclick={() => navigateTo(crumb.path)}
                  title={crumb.label || "Bucket"}
                >
                  {crumb.label || "Bucket"}
                </button>
              {/each}
            </nav>
          </div>
        </div>

        {#if permissions.upload && showUploadQueue && uploadQueue.length > 0}
          <div class="px-4 pb-4">
            <div class="rounded-xl border border-white/10 bg-black/30 p-3">
              <div class="flex items-center justify-between">
                <div class="text-xs uppercase tracking-wide text-zinc-500">Upload queue</div>
                <div class="flex items-center gap-3 text-xs text-zinc-500">
                  <span>{uploadQueue.some(j => j.status === "uploading") ? "Uploading" : "Queued"}</span>
                  {#if uploadQueue.every(j => j.status === "done")}
                    <button
                      type="button"
                      class="text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors"
                      onclick={() => (showUploadQueue = false)}
                    >
                      Dismiss
                    </button>
                  {/if}
                </div>
              </div>
              <div class="mt-2 space-y-2">
                {#each uploadQueue.slice(0, 6) as job (job.id)}
                  <div class="rounded-lg border border-white/10 bg-black/40 px-3 py-2">
                    <div class="flex items-center justify-between gap-3">
                      <div class="min-w-0">
                        <div class="text-xs font-mono text-zinc-200 break-all">{job.name}</div>
                        <div class="mt-0.5 text-[11px] text-zinc-500">{formatBytes(job.size)}</div>
                      </div>
                      <div class="text-[11px]">
                        {#if job.status === "uploading"}
                          <span class="text-lime-300">Uploading</span>
                        {:else if job.status === "done"}
                          <span class="text-emerald-300">Uploaded</span>
                        {:else if job.status === "error"}
                          <span class="text-rose-300">Failed</span>
                        {:else}
                          <span class="text-zinc-400">Queued</span>
                        {/if}
                      </div>
                    </div>
                    {#if job.status === "uploading"}
                      <div class="mt-2">
                        <div class="h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div
                            class="h-full bg-lime-500 transition-all duration-300"
                            style="width: {job.progress || 0}%"
                          ></div>
                        </div>
                        <div class="mt-1 text-[11px] text-lime-300">{job.progress || 0}%</div>
                      </div>
                    {:else if job.status === "error"}
                      <div class="mt-1 text-[11px] text-rose-300 break-words">{job.error}</div>
                    {/if}
                  </div>
                {/each}
              </div>
            </div>
          </div>
        {/if}

        <div class="px-4 pt-4">
          <AlertStack {alerts} onDismiss={dismissAlert} />
        </div>

        <!-- File List -->
        <div class="pb-4">
          {#if viewMode === "list"}
            <div class="px-4 py-2 text-xs uppercase tracking-wide text-zinc-500 flex items-center justify-between">
              <button
                type="button"
                class="flex-1 text-left inline-flex items-center gap-2 hover:text-zinc-300 transition-colors"
                onclick={() => toggleSort("name")}
              >
                Name
                <Icon
                  icon={sortDir === "asc" ? arrowUpIcon : arrowDownIcon}
                  width="12"
                  height="12"
                  class={sortBy === "name" ? "text-sky-400" : "text-zinc-600"}
                />
              </button>
              <button
                type="button"
                class="w-24 text-right inline-flex items-center justify-end gap-2 hover:text-zinc-300 transition-colors"
                onclick={() => toggleSort("size")}
              >
                Size
                <Icon
                  icon={sortDir === "asc" ? arrowUpIcon : arrowDownIcon}
                  width="12"
                  height="12"
                  class={sortBy === "size" ? "text-sky-400" : "text-zinc-600"}
                />
              </button>
              <span class="w-28 text-right"></span>
            </div>

            {#each folders as folder}
              <div class="w-full px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors flex items-center gap-3">
                <button
                  type="button"
                  class="flex items-center gap-3 min-w-0 flex-1 text-left"
                  onclick={() => navigateTo(folder)}
                  title={getFolderName(folder)}
                >
                  <Icon icon={folderIcon} width="24" height="24" class="text-sky-400 flex-shrink-0" />
                  <span class="text-sm text-zinc-300 truncate">{getFolderName(folder)}</span>
                </button>
                <span class="w-24 text-right text-xs text-zinc-500">{getFolderSizeLabel(folder)}</span>
                <div class="w-28 flex items-center justify-end gap-1">
                  <button
                    type="button"
                    class="p-2 rounded-lg hover:bg-white/10 transition-colors"
                    title="Open folder"
                    onclick={() => navigateTo(folder)}
                  >
                    <Icon icon={openInNewIcon} width="16" height="16" />
                  </button>
                  {#if permissions.delete}
                    <button
                      type="button"
                      class="p-2 rounded-lg hover:bg-white/10 transition-colors text-rose-400 hover:text-rose-300"
                      title="Delete folder"
                      onclick={() => requestDelete({ key: folder, type: "folder" })}
                    >
                      <Icon icon={deleteIcon} width="16" height="16" />
                    </button>
                  {/if}
                </div>
              </div>
            {/each}

            {#each objects as obj}
              {@const meta = getFileIconMeta(obj.key, obj.content_type)}
              <div class={`w-full px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors flex items-center gap-3 ${selectedFile?.key === obj.key ? "bg-sky-500/10" : ""}`}>
                <button
                  type="button"
                  class="flex items-center gap-3 min-w-0 flex-1 text-left"
                  onclick={() => selectFile(obj)}
                  title={getFileName(obj.key)}
                >
                  <Icon icon={meta.icon} width="20" height="20" class={meta.color} />
                  <span class="text-sm text-zinc-300 truncate">{getFileName(obj.key)}</span>
                </button>
                <span class="w-24 text-right text-xs text-zinc-500">{getObjectSizeLabel(obj)}</span>
                <div class="w-28 flex items-center justify-end gap-1">
                  {#if permissions.download}
                    <button
                      type="button"
                      class="p-2 rounded-lg hover:bg-white/10 transition-colors"
                      title="Open in new tab"
                      onclick={() => openSignedUrl(obj, "view")}
                    >
                      <Icon icon={openInNewIcon} width="16" height="16" />
                    </button>
                    <button
                      type="button"
                      class="p-2 rounded-lg hover:bg-white/10 transition-colors"
                      title="Download"
                      onclick={() => openSignedUrl(obj, "download")}
                    >
                      <Icon icon={downloadIcon} width="16" height="16" />
                    </button>
                  {/if}
                  {#if permissions.delete}
                    <button
                      type="button"
                      class="p-2 rounded-lg hover:bg-white/10 transition-colors text-rose-400 hover:text-rose-300"
                      title="Delete"
                      onclick={() => requestDelete({ key: obj.key, type: "file" })}
                    >
                      <Icon icon={deleteIcon} width="16" height="16" />
                    </button>
                  {/if}
                </div>
              </div>
            {/each}
          {:else}
            <!-- Grid View -->
            <div class="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {#each folders as folder}
                <div class="p-4 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] transition-colors flex flex-col items-center gap-2 overflow-hidden min-w-0">
                  <button
                    type="button"
                    class="flex flex-col items-center gap-2 w-full min-w-0"
                    onclick={() => navigateTo(folder)}
                    title={getFolderName(folder)}
                  >
                    <Icon icon={folderIcon} width="52" height="52" class="text-sky-400 flex-shrink-0" />
                    <span class="text-xs text-zinc-400 truncate max-w-full text-center">{getFolderName(folder)}</span>
                  </button>
                  <span class="text-xs text-zinc-600">{getFolderSizeLabel(folder)}</span>
                  <div class="flex items-center gap-1">
                    <button
                      type="button"
                      class="p-2 rounded-lg hover:bg-white/10 transition-colors"
                      title="Open folder"
                      onclick={() => navigateTo(folder)}
                    >
                      <Icon icon={openInNewIcon} width="16" height="16" />
                    </button>
                    {#if permissions.delete}
                      <button
                        type="button"
                        class="p-2 rounded-lg hover:bg-white/10 transition-colors text-rose-400 hover:text-rose-300"
                        title="Delete folder"
                        onclick={() => requestDelete({ key: folder, type: "folder" })}
                      >
                        <Icon icon={deleteIcon} width="16" height="16" />
                      </button>
                    {/if}
                  </div>
                </div>
              {/each}

              {#each objects as obj}
                {@const meta = getFileIconMeta(obj.key, obj.content_type)}
                {@const isImage = isImageKey(obj.key, obj.content_type)}
                <div class={`p-4 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] transition-colors flex flex-col items-center gap-2 overflow-hidden min-w-0 ${selectedFile?.key === obj.key ? "ring-2 ring-sky-400/50" : ""}`}>
                  <button
                    type="button"
                    class="flex flex-col items-center gap-2 w-full min-w-0"
                    onclick={() => selectFile(obj)}
                    title={getFileName(obj.key)}
                  >
                    {#if isImage && previewUrls[obj.key]}
                      <img
                        src={previewUrls[obj.key]}
                        alt={getFileName(obj.key)}
                        class="h-20 w-20 rounded-lg object-cover border border-white/10 flex-shrink-0"
                        loading="lazy"
                      />
                    {:else}
                      <Icon icon={meta.icon} width="40" height="40" class={`${meta.color} flex-shrink-0`} />
                    {/if}
                    <span class="text-xs text-zinc-400 truncate max-w-full text-center">{getFileName(obj.key)}</span>
                  </button>
                  <span class="text-xs text-zinc-600">{getObjectSizeLabel(obj)}</span>
                  <div class="flex items-center gap-1">
                    {#if permissions.download}
                      <button
                        type="button"
                        class="p-2 rounded-lg hover:bg-white/10 transition-colors"
                        title="Open in new tab"
                        onclick={() => openSignedUrl(obj, "view")}
                      >
                        <Icon icon={openInNewIcon} width="16" height="16" />
                      </button>
                      <button
                        type="button"
                        class="p-2 rounded-lg hover:bg-white/10 transition-colors"
                        title="Download"
                        onclick={() => openSignedUrl(obj, "download")}
                      >
                        <Icon icon={downloadIcon} width="16" height="16" />
                      </button>
                    {/if}
                    {#if permissions.delete}
                      <button
                        type="button"
                        class="p-2 rounded-lg hover:bg-white/10 transition-colors text-rose-400 hover:text-rose-300"
                        title="Delete"
                        onclick={() => requestDelete({ key: obj.key, type: "file" })}
                      >
                        <Icon icon={deleteIcon} width="16" height="16" />
                      </button>
                    {/if}
                  </div>
                </div>
              {/each}
            </div>
          {/if}

          {#if folders.length === 0 && objects.length === 0 && !loading}
            <div class="p-8 text-center text-zinc-600">
              This folder is empty
            </div>
          {/if}

          <div bind:this={loadMoreSentinel}></div>

          {#if hasMore}
            <div class="p-4 text-center">
              <button
                type="button"
                class="ui-btn"
                onclick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading..." : "Load More"}
              </button>
            </div>
          {/if}
        </div>
      </div>
    </div>

    <!-- File Detail -->
    {#if selectedFile}
      <div class="lg:w-[520px] flex-shrink-0">
        <FileDetail
          {apiBase}
          {apiKey}
          {bucket}
          file={selectedFile}
          detail={fileDetail}
          {permissions}
          onClose={() => { selectedFile = null; fileDetail = null; }}
          onDelete={() => requestDelete({ key: selectedFile.key, type: "file" })}
          onError={pushError}
          {onUnauthorized}
        />
      </div>
    {/if}
  </div>

  {#if showDeleteModal}
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        class="absolute inset-0 bg-black/70"
        onclick={cancelDelete}
        aria-label="Close delete modal"
      ></button>
      <div class="relative ui-card ui-card--solid w-full max-w-xl p-6">
        <div class="flex items-start gap-3">
          <div class="h-10 w-10 rounded-lg bg-rose-600 flex items-center justify-center text-white">
            <Icon icon={deleteIcon} width="18" height="18" />
          </div>
          <div class="flex-1">
            <h3 class="text-lg font-semibold text-white">Confirm deletion</h3>
            <p class="mt-1 text-sm text-zinc-400">
              {#if deleteIsFolder}
                This will remove every file under this folder. This action cannot be undone.
              {:else}
                This file will be permanently deleted. This action cannot be undone.
              {/if}
            </p>
            <p class="mt-2 text-xs text-zinc-500 break-all">
              {deleteTarget?.key}
            </p>
          </div>
        </div>
        <div class="mt-6 flex items-center justify-end gap-2">
          <button type="button" class="ui-btn" onclick={cancelDelete} disabled={deleteBusy}>Cancel</button>
          <button
            type="button"
            class="ui-btn-accent"
            onclick={confirmDelete}
            disabled={deleteBusy}
          >
            {deleteBusy ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  {/if}
</div>
